/**
 * src/components/spatial/spatialBlockerText.ts
 *
 * Refusal codes as sentences an operator can act on.
 *
 * Shared by the simulator and the chat panel so the same refusal reads
 * the same way wherever it surfaces. A reason code is a fine wire
 * format and a poor thing to show somebody: `webrtc_policy_not_set`
 * looks like a bug, whereas "unset is not permission" tells them what
 * to go and change.
 */

/** Reasons rendered as sentences; anything else falls back to the raw id. */
export const BLOCKER_TEXT: Record<string, string> = {
  media_presence_disabled: 'Media presence is switched off. Enable it above and save, then try again.',
  ai_presence_disabled: 'AI presence is switched off. Enable it above and save, then try again.',
  surface_presence_disabled: 'Surface presence is switched off.',
  tts_disabled: 'Speech is switched off for this object.',
  not_live: 'The object is not broadcasting. Start the broadcast first.',
  trigger_not_configured: 'This trigger is not one of the configured broadcast modes.',
  object_not_registered: 'The object is not registered on chain, so it has no authority to act under.',
  object_not_found: 'GeoLink has no object with this id.',
  cooldown_active: 'Still inside the configured cooldown.',
  geolink_unavailable: 'GeoLink could not be reached, so its configuration could not be read.',
  geolink_forbidden: 'GeoLink refused to return this object’s configuration to GeoTrust.',
  live_broadcast_not_allowed: 'Live broadcasting is not allowed on this object.',
  no_broadcast_anchor: 'The object has no centroid or anchor point to broadcast from.',
  country_unknown: 'The object has no resolvable country, so no policy applies.',
  webrtc_policy_not_set: 'This country has no WebRTC policy recorded. Unset is not permission.',
  webrtc_disallowed_in_country: 'Live media is disallowed in this country.',
  denied_by_compliance_list: 'The authorizing wallet is on a compliance deny-list.',
  no_standing_authority: 'No wallet has been recorded as the standing authority.',
  wallet_cannot_manage_object: 'The authorizing wallet no longer holds manage rights on chain.',
  authority_unreadable: 'The chain answer was incomplete, so authority cannot be confirmed.',
  authority_read_failed: 'The authority check could not reach the chain.',
  execution_rule_not_satisfied: 'The execution rule does not currently hold.',
  audience_not_allowed: 'This participant type is not in the configured audience.',
  wallet_required: 'A connected wallet is required and none was supplied.',
  out_of_range: 'The participant is outside the playback radius.',

  // Private conversation.
  object_not_interactive: 'This object is set to passive, so it will not hold a conversation.',
  humans_not_admitted: 'The configured audience does not admit people.',
  agents_not_admitted: 'The configured audience does not admit AI agents.',
  too_many_sessions: 'This participant already has as many open conversations as the object allows.',
  session_not_found: 'The conversation has ended or expired. Open a new one.',
  empty_message: 'Nothing to send.',
  message_too_long: 'That message is longer than the object accepts.',
  message_cap_reached: 'This conversation has used all the messages it was allotted.',
  rate_limited: 'Sent too quickly. Wait a moment and try again.',
  visitor_message_quota: 'You have asked this object as much as it answers in one stretch.',
  wallet_message_quota: 'This wallet has asked this object as much as it answers in one stretch.',
  object_message_quota: 'This object is busy answering other people. Try again shortly.',

  // Surface and occasion.
  content_not_allowed: 'That content is not on the operator’s approved list, so it was refused.',
  no_content_ref: 'No content was named, and the object has no default to fall back on.',
};

export function blockerText(code: string): string {
  return BLOCKER_TEXT[code] || code;
}

/**
 * A refusal as a sentence.
 *
 * A server message is preferred when there is one, because it carries
 * the specifics the reason code cannot.
 */
export function failureText(result: { reason: string; [key: string]: unknown }): string {
  return typeof result.message === 'string' && result.message
    ? result.message
    : blockerText(result.reason);
}

/**
 * A refusal, plus when it stops being one.
 *
 * A rationed refusal without its timing is an unanswerable question:
 * the visitor is told no and left to guess whether to wait a second or
 * an hour. Rounded up to the whole second, because "try again in 0
 * seconds" is not an instruction.
 */
export function retryText(result: { reason: string; retryAfterMs?: unknown; [key: string]: unknown }): string {
  const base = failureText(result);
  const ms = Number(result.retryAfterMs);
  if (!Number.isFinite(ms) || ms <= 0) return base;

  const seconds = Math.ceil(ms / 1000);
  if (seconds < 90) return `${base} Try again in ${seconds}s.`;
  return `${base} Try again in about ${Math.ceil(seconds / 60)} min.`;
}
